/**
 * Typed client for /api/field/* and /api/admin/field/*.
 *
 * Mirrors hy_sales/schemas/field.py — keep in lockstep.  When the
 * backend Pydantic shapes change, update the interfaces here in the
 * same PR.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { nearHome, todayScore, TODAY_PLAN_LIMIT } from '../pages/field/todayScore';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from './client';
import type { FollowUpAccount, FollowUpTrackerResponse } from './depletions';

// =====================================================================
// Shared literal types (mirror the DB CHECK constraints)
// =====================================================================
// 'email' + 'event' added by backend migration
// 017_visit_notes_add_email_event_channels.sql.
export type VisitChannel = 'visit' | 'call' | 'email' | 'event';
export type VisitOutcome =
  | 'ordered'
  | 'follow_up_needed'
  | 'no_response'
  | 'declined'
  | 'info_only';

// =====================================================================
// Rep profile
// =====================================================================
export interface RepProfile {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  home_address: string | null;
  home_city: string | null;
  home_state: string | null;
  home_zip: string | null;
  phone: string | null;
  territory_states: string[];
  is_active: boolean;
}

export interface RepProfileSelfUpdate {
  home_address?: string | null;
  home_city?: string | null;
  home_state?: string | null;
  home_zip?: string | null;
  phone?: string | null;
}

export interface RepProfileAdminUpdate extends RepProfileSelfUpdate {
  territory_states?: string[];
  is_active?: boolean;
}

// =====================================================================
// Accounts + Today
// =====================================================================
export interface FieldAccountSummary {
  account_id: number;
  name: string;
  address: string | null;
  city: string | null;
  state_code: string | null;
  zip_code: string | null;
  premises_type: string | null;

  last_visit_date: string | null;
  last_outcome: VisitOutcome | null;
  last_channel: VisitChannel | null;
  last_note_excerpt: string | null;

  days_since_last_visit: number | null;
  is_pinned: boolean;
  depletions_flagged: boolean;
  priority_score: number;

  next_eligible_date: string | null;

  // Depletions metrics — null when no shipments on record for this
  // account.  total_9l_12mo is a Pydantic Decimal serialized as string
  // (preserve precision); parse to Number at display only.
  last_active_month: string | null;
  days_since_last_shipment: number | null;
  activity_bucket: string | null;
  total_9l_12mo: string | null;
  product_count: number;
  customer_since: string | null;

  /** 'rep' = manually added, not yet confirmed by a depletions
   *  file. Promoted to 'vip' by the ingest on a natural-key match. */
  source: AccountSource;

  /* Contacts are NOT here — they hang off FieldAccountDetail. Only
     the account page shows them, and carrying them on every row of
     every list would cost a query on the hot path to feed nothing. */

  /** When the account was created — for a hand-added account, when
   *  the rep entered it. */
  added_at: string | null;
  /** Who added this by hand. null for feed-created accounts. */
  added_by_name: string | null;
}

// =====================================================================
// Visit notes
// =====================================================================
/** What a rep-reported quantity counts. */
export type VisitProductUnit = 'cases' | 'bottles';

/** A product line on a visit note.
 *
 *  REP-REPORTED, not distributor data. Never sum these with the 9L
 *  figures that come from depletions.facts. */
export interface VisitNoteProduct {
  product_id: number;
  product_name: string;
  /** Serialized Decimal, or null for "discussed, no number". */
  quantity: string | null;
  unit: VisitProductUnit;
}

export interface VisitNoteProductCreate {
  product_id: number;
  quantity?: number | null;
  unit?: VisitProductUnit;
}

/** One entry in the visit-note product picker. */
export interface ProductOption {
  product_id: number;
  name: string;
  category: string | null;
}

export interface ProductOptionsResponse {
  products: ProductOption[];
}

export interface VisitNote {
  id: number;
  account_id: number;
  rep_id: string;
  rep_name: string;
  visit_date: string;
  channel: VisitChannel;
  outcome: VisitOutcome;
  note_text: string;
  /** Empty for notes logged before line items existed. */
  products: VisitNoteProduct[];
  created_at: string;
  /** When the note was changed after logging; null if never. */
  edited_at: string | null;
  /** Who made the latest edit — the real admin if they edited while
   *  acting as the rep. */
  edited_by_name: string | null;
  /** The latest edit was by someone other than the note's author. */
  edited_by_someone_else: boolean;
  /** Whether the caller may edit this note (its author, or an admin). */
  can_edit: boolean;
}

/** The editable part of a logged visit — what the edit form prefills
 *  from. Both a VisitNote and a My Logs visit entry map onto it. */
export interface VisitNoteDraft {
  id: number;
  visit_date: string;
  channel: VisitChannel;
  outcome: VisitOutcome;
  note_text: string;
  products: VisitNoteProduct[];
}

/** Body of both create and edit — an edit replaces the whole note,
 *  product lines included. */
export interface VisitNoteCreate {
  visit_date: string;
  channel: VisitChannel;
  outcome: VisitOutcome;
  note_text: string;
  products?: VisitNoteProductCreate[];
}

/** One person a rep deals with at an account.
 *
 *  A person gets a record; a phone number doesn't. Phones and emails
 *  are plain ordered lists on the person, best first — nothing ever
 *  links to a single number, so giving each one an identity would buy
 *  a third level of nesting and return nothing. */
export interface AccountContact {
  id: number;
  name: string | null;
  /** Free text: 'Owner', 'Bar manager', 'the guy who signs for
   *  deliveries'. */
  role: string | null;
  phones: string[];
  emails: string[];
  notes: string | null;
  /** null for contacts the 029 backfill created from the old
   *  single-contact columns — those have no known author. */
  added_by_name: string | null;
}

/** A contact on the way IN.
 *
 *  `id` present = edit this row in place; absent = create a new one.
 *  Anything omitted from the submitted list is deleted. */
export interface AccountContactInput {
  id?: number;
  name?: string | null;
  role?: string | null;
  phones?: string[];
  emails?: string[];
  notes?: string | null;
}

export interface AccountContactsResponse {
  contacts: AccountContact[];
}

export interface FieldAccountDetail {
  summary: FieldAccountSummary;
  notes: VisitNote[];
  /** Everyone the reps deal with here, best first. */
  contacts: AccountContact[];
}

export interface PinResponse {
  account_id: number;
  is_pinned: boolean;
}

// =====================================================================
// Admin
// =====================================================================
export interface AdminRepRow {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  territory_states: string[];
  is_active: boolean;
  visit_count_30d: number;
  last_visit_at: string | null;
}

export interface AdminRepsResponse {
  items: AdminRepRow[];
}

export interface AdminActivityRow {
  note_id: number;
  rep_id: string;
  rep_name: string;
  account_id: number;
  account_name: string;
  account_state: string | null;
  visit_date: string;
  channel: VisitChannel;
  outcome: VisitOutcome;
  /** Server-truncated. Prefer `note_text` and clamp in the UI, so a
   *  long note can be expanded rather than losing its end. */
  note_excerpt: string;
  note_text: string;
  created_at: string;
}

export interface AdminActivityResponse {
  items: AdminActivityRow[];
  total: number;
  limit: number;
  offset: number;
}

// =====================================================================
// Endpoint wrappers — rep-facing
// =====================================================================
export function fetchMyRepProfile(): Promise<RepProfile> {
  return apiGet<RepProfile>('sales', '/api/field/me');
}

export function updateMyRepProfile(payload: RepProfileSelfUpdate): Promise<RepProfile> {
  return apiPatch<RepProfile>('sales', '/api/field/me', payload);
}

export function fetchAccountDetail(accountId: number): Promise<FieldAccountDetail> {
  return apiGet<FieldAccountDetail>('sales', `/api/field/accounts/${accountId}`);
}

/** Replace the account's contacts with exactly this list.
 *
 *  Whole-set, not a patch: the editor works on the list as a unit and
 *  sends back what it should now be. The server works out what that
 *  means in creates, updates and deletes. */
export function saveAccountContacts(
  accountId: number,
  contacts: AccountContactInput[],
): Promise<AccountContactsResponse> {
  return apiPut<AccountContactsResponse>('sales', `/api/field/accounts/${accountId}/contacts`, {
    contacts,
  });
}

export function createVisitNote(accountId: number, payload: VisitNoteCreate): Promise<VisitNote> {
  return apiPost<VisitNote>('sales', `/api/field/accounts/${accountId}/notes`, payload);
}

export function updateVisitNote(
  accountId: number,
  noteId: number,
  payload: VisitNoteCreate,
): Promise<VisitNote> {
  return apiPut<VisitNote>('sales', `/api/field/accounts/${accountId}/notes/${noteId}`, payload);
}

// =====================================================================
// Rep-added accounts
// =====================================================================

/** Where an account came from. */
export type AccountSource = 'vip' | 'rep';

export type AccountEventType =
  | 'created_by_rep'
  | 'appeared_in_vip'
  | 'possible_duplicate'
  | 'merged_from'
  | 'merged_into';

/** A possible existing match for a name the rep is typing. */
export interface AccountMatch {
  account_id: number;
  name: string;
  address: string | null;
  city: string | null;
  state_code: string | null;
  source: AccountSource;
  /** 0-1 trigram similarity on the normalised name. */
  similarity: number;
  /** Names differ only by case or punctuation — creating alongside one
   *  of these is almost always a mistake. */
  is_exact_normalised: boolean;
}

export interface AccountMatchesResponse {
  matches: AccountMatch[];
}

export interface CreateAccountPayload {
  name: string;
  /** Blank cards, blank rows and repeats are dropped server-side;
   *  order is kept, because the first entry is the one to try first. */
  contacts?: AccountContactInput[];
  address?: string | null;
  city?: string | null;
  state_code?: string | null;
  zip_code?: string | null;
  premises_type?: 'ON' | 'OFF' | null;
  notes?: string | null;
  /** Set once the rep has seen the near matches and says it's new. */
  confirm_despite_matches?: boolean;
}

export interface AccountEvent {
  id: number;
  event_type: AccountEventType;
  /** null for events raised by the ingest — no human actor. */
  actor_name: string | null;
  occurred_at: string;
  /** Rendered server-side so every surface phrases it identically. */
  summary: string;
}

export interface AccountTimelineResponse {
  events: AccountEvent[];
}

/** Type-ahead lookup for the account-name picker.
 *
 *  Not scoped by territory OR by the form's state field — the point is
 *  to catch an account that already exists somewhere the rep isn't
 *  looking. Each result carries its own state so they can tell. */
export function searchAccounts(q: string): Promise<AccountMatchesResponse> {
  return apiGet<AccountMatchesResponse>('sales', '/api/field/accounts/search', { q });
}

/** Create an account the depletions feed doesn't have.
 *
 *  Throws ApiError 409 with `detail.matches` when near matches exist
 *  and `confirm_despite_matches` wasn't set — the caller shows them
 *  and re-submits with the flag. */
export function createAccount(payload: CreateAccountPayload): Promise<FieldAccountDetail> {
  return apiPost<FieldAccountDetail>('sales', '/api/field/accounts', payload);
}

/** What a rep did. One feed, because "what have I done" doesn't sort
 *  itself into categories in anyone's head. */
export type LogEntryKind = 'visit' | 'flag' | 'skip';

/** One entry in a rep's own cross-account activity feed.
 *
 *  A union over visit notes, review flags and skips, flattened into
 *  one shape — fields that only apply to one kind are null. */
export interface MyLogEntry {
  /** Stable across kinds ("visit:12", "flag:345"). */
  id: string;
  kind: LogEntryKind;

  account_id: number;
  account_name: string;
  /** Street + city, so two branches of the same chain are
   *  distinguishable without opening either. */
  account_address: string | null;
  account_city: string | null;
  account_state: string | null;
  account_is_manual: boolean;

  /** The day it HAPPENED — drives grouping, so a backdated visit lands
   *  on the day of the visit rather than the day it was typed. */
  occurred_on: string;
  /** When it was actually recorded. */
  logged_at: string;

  // visit only
  /** The visit note's id, for editing it from the log. */
  note_id: number | null;
  /** Same meaning as on VisitNote. */
  edited_at: string | null;
  edited_by_name: string | null;
  edited_by_someone_else: boolean;
  channel: VisitChannel | null;
  outcome: VisitOutcome | null;
  note_text: string | null;
  products: VisitNoteProduct[];

  // flag only
  reason: string | null;
}

export interface MyLogsResponse {
  items: MyLogEntry[];
  total: number;
  limit: number;
  offset: number;
  /** Per-kind totals for the filter chips — fixed regardless of the
   *  page or filter, so the counts don't move as you read. */
  visit_count: number;
  flag_count: number;
  skip_count: number;
}

/** Everything this rep has done, across every account.
 *
 *  The per-account timeline answers "what happened at this bar?"; this
 *  answers "what have I actually done?", which otherwise means opening
 *  accounts one at a time. */
export function fetchMyLogs(
  limit: number,
  offset: number,
  kind?: LogEntryKind,
): Promise<MyLogsResponse> {
  return apiGet<MyLogsResponse>('sales', '/api/field/my-logs', { limit, offset, kind });
}

/** Record that the rep dismissed an account from Today's Plan.
 *
 *  Fire-and-forget, and deliberately does NOT change what skipping
 *  does — the account is back tomorrow either way. This only leaves a
 *  trace so the skip shows up in My Logs. */
export function recordSkip(accountId: number): Promise<void> {
  return apiPost<void>('sales', `/api/field/accounts/${accountId}/skip`, {});
}

/** The SKU catalog for the visit-note product picker. Small and
 *  stable, so it's fetched whole and cached long. */
export function fetchProductOptions(): Promise<ProductOptionsResponse> {
  return apiGet<ProductOptionsResponse>('sales', '/api/field/products');
}

/** One hand-added account, with anything it might duplicate. */
export interface ManualAccountRow {
  account: FieldAccountSummary;
  /** Empty for most accounts — the bar for proposing a duplicate is
   *  deliberately high, because a queue of wrong suggestions is worse
   *  than no queue. */
  suggestions: AccountMatch[];
}

export interface ManualAccountsResponse {
  items: ManualAccountRow[];
  /** How many rows have an unresolved suggestion. */
  needs_review: number;
}

export function fetchManualAccounts(): Promise<ManualAccountsResponse> {
  return apiGet<ManualAccountsResponse>('sales', '/api/field/manual-accounts');
}

/** Record that two accounts are NOT the same place. Suppresses the
 *  pair from future suggestions, in both directions. */
export function dismissDuplicate(accountId: number, otherAccountId: number): Promise<void> {
  return apiPost<void>(
    'sales',
    `/api/field/accounts/${accountId}/dismiss-duplicate/${otherAccountId}`,
    {},
  );
}

export function fetchAccountTimeline(accountId: number): Promise<AccountTimelineResponse> {
  return apiGet<AccountTimelineResponse>('sales', `/api/field/accounts/${accountId}/timeline`);
}

export function pinAccount(accountId: number): Promise<PinResponse> {
  return apiPost<PinResponse>('sales', `/api/field/accounts/${accountId}/pin`, {});
}

export function unpinAccount(accountId: number): Promise<PinResponse> {
  return apiDelete<PinResponse>('sales', `/api/field/accounts/${accountId}/pin`);
}

// =====================================================================
// Flag for admin review — rep-driven escape valve for account-level
// issues that don't fit the structured visit outcomes (permanent
// closure, ownership change, distribution misroute).  Flag is
// captured in the auth audit log; no new queryable surface yet.
// =====================================================================
export interface AccountFlagCreate {
  reason: string;
}

export interface AccountFlagResponse {
  account_id: number;
  flagged_at: string;
}

export function flagAccountForReview(
  accountId: number,
  payload: AccountFlagCreate,
): Promise<AccountFlagResponse> {
  return apiPost<AccountFlagResponse>('sales', `/api/field/accounts/${accountId}/flag`, payload);
}

export interface AccountFlagHistoryItem {
  id: number;
  rep_id: string | null;
  rep_name: string;
  reason: string;
  flagged_at: string;
}

export interface AccountFlagHistoryResponse {
  items: AccountFlagHistoryItem[];
}

export function fetchAccountFlags(accountId: number): Promise<AccountFlagHistoryResponse> {
  return apiGet<AccountFlagHistoryResponse>('sales', `/api/field/accounts/${accountId}/flags`);
}

// =====================================================================
// Endpoint wrappers — admin
// =====================================================================
export function fetchAdminReps(): Promise<AdminRepsResponse> {
  return apiGet<AdminRepsResponse>('sales', '/api/admin/field/reps');
}

export function fetchAdminRep(userId: string): Promise<RepProfile> {
  return apiGet<RepProfile>('sales', `/api/admin/field/reps/${userId}`);
}

export function updateAdminRep(
  userId: string,
  payload: RepProfileAdminUpdate,
): Promise<RepProfile> {
  return apiPatch<RepProfile>('sales', `/api/admin/field/reps/${userId}`, payload);
}

export function fetchAdminActivity(
  limit?: number,
  offset?: number,
): Promise<AdminActivityResponse> {
  return apiGet<AdminActivityResponse>('sales', '/api/admin/field/activity', {
    limit,
    offset,
  });
}

// =====================================================================
// Follow-up tracker — depletions buckets scoped to the rep's territory.
// Mirrors useFollowUpTracker in api/depletions.ts but hits the
// territory-filtered Field endpoint instead.  Drop-in replacement for
// the existing FollowUpTracker component via its `useDataSource` prop.
// =====================================================================
/**
 * Bucketed follow-up accounts.
 *
 * With no `states`, the backend scopes to the rep's assigned territory —
 * which is what Today's Plan and an unfiltered My Territories show.
 *
 * Passing states widens it beyond the territory on purpose: a rep
 * travelling to a state they do not cover needs to find the accounts
 * there and log the visits they are actually making. The states are part
 * of the query key, so each selection is cached separately rather than
 * clobbering the territory view.
 */
export function useFieldFollowUp(states?: string[]): UseQueryResult<FollowUpTrackerResponse> {
  const scope = (states ?? []).slice().sort();
  const query = scope.length > 0 ? `?states=${encodeURIComponent(scope.join(','))}` : '';
  return useQuery({
    queryKey: ['field', 'follow-up', scope],
    queryFn: () => apiGet<FollowUpTrackerResponse>('sales', `/api/field/follow-up${query}`),
    staleTime: 30_000,
  });
}

/** Mirrors hy_sales AccountsResponse. */
export interface AccountsResponse {
  items: FieldAccountSummary[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * Accounts on file with no shipment ever reported.
 *
 * They appear nowhere else: the tracker INNER JOINs depletions facts and
 * buckets by days-since-last-depletion, which these accounts do not
 * have. Rep-added ones are excluded server-side — those have their own
 * tab.
 *
 * Takes the same optional states as the tracker, so a rep can look at a
 * state they are visiting rather than only their own.
 */
export function useNoShipmentAccounts(states?: string[]): UseQueryResult<AccountsResponse> {
  const scope = (states ?? []).slice().sort();
  const query = scope.length > 0 ? `?states=${encodeURIComponent(scope.join(','))}` : '';
  return useQuery({
    queryKey: ['field', 'no-shipment-accounts', scope],
    queryFn: () => apiGet<AccountsResponse>('sales', `/api/field/no-shipment-accounts${query}`),
    staleTime: 60_000,
  });
}

export interface FieldStatesResponse {
  /** Every state with active accounts. */
  states: string[];
  /** The caller's own assignment — listed first in the filter. */
  territory_states: string[];
}

/**
 * States available in the filter.
 *
 * Long-lived: the set of states the company sells into changes with the
 * depletions feed, not with anything the rep does in a session.
 */
export function useFieldStates(): UseQueryResult<FieldStatesResponse> {
  return useQuery({
    queryKey: ['field', 'states'],
    queryFn: () => apiGet<FieldStatesResponse>('sales', '/api/field/states'),
    staleTime: 10 * 60_000,
  });
}

// =====================================================================
// Today's Plan — single synthesized "Today's Plan" bucket containing
// the rep's top-N curated accounts, ranked across every real bucket
// by the todayScore function.  Drop-in replacement for the
// FollowUpTracker's data source so the same table chrome (rows,
// filters, sort, search, rep actions) renders the curated list.
// =====================================================================
export const TODAY_PLAN_BUCKET_LABEL = "Today's Plan";

export function useTodaysPlanData(): UseQueryResult<FollowUpTrackerResponse> {
  // Reuse the same backend round-trip as useFieldFollowUp — TanStack
  // dedupes on query-key, so the two hooks share one fetch.
  const inner = useFieldFollowUp();
  // Rep profile drives the geographic boost — accounts in the rep's
  // home ZIP / metro surface first.  Same query key as
  // fetchMyRepProfile in FieldPage so the two share one fetch.
  const profile = useQuery({
    queryKey: ['field', 'me'],
    queryFn: fetchMyRepProfile,
    staleTime: 60_000,
  });
  // Synthesize a one-bucket response by ranking every account across
  // buckets and keeping the top N.  Memo-ed via select so re-renders
  // stay cheap.
  return useQuery({
    queryKey: ['field', 'today-plan', inner.dataUpdatedAt, profile.dataUpdatedAt],
    queryFn: () => {
      if (!inner.data) throw new Error('no upstream data');
      const home = profile.data
        ? {
            home_zip: profile.data.home_zip,
            home_state: profile.data.home_state,
          }
        : null;
      const all: {
        account: FollowUpAccount;
        bucketLabel: string;
        score: number;
        isLocal: boolean;
      }[] = [];
      for (const bucket of inner.data.buckets) {
        for (const acct of bucket.accounts) {
          all.push({
            account: acct,
            bucketLabel: bucket.label,
            score: todayScore(acct, bucket.label, home),
            isLocal: home ? nearHome(acct, home) !== null : false,
          });
        }
      }
      // When the rep has a home ZIP, default the day's list to their
      // home metro (ZIP-3) — that's the "show me Jacksonville
      // accounts" intent.  Fall back to the full territory only when
      // there aren't enough local picks to fill a useful day; the
      // declining-account / pinned signals still surface from
      // elsewhere via My Territories.
      const MIN_LOCAL_PICKS = 5;
      const localPool = all.filter((a) => a.isLocal && a.score >= 0);
      const useLocalOnly = home?.home_zip != null && localPool.length >= MIN_LOCAL_PICKS;
      const pool = useLocalOnly ? localPool : all;
      pool.sort((a, b) => b.score - a.score);
      const picked = pool.filter((a) => a.score >= 0).slice(0, TODAY_PLAN_LIMIT);
      // We keep the SOURCE bucket label inside last_outcome's
      // sibling field so the row's "Last Order" / Action column
      // still shows the right per-account context — we don't merge
      // them into one fake bucket.  The picked list comes back with
      // the original FollowUpAccount shape untouched.
      const totalCases = picked.reduce((sum, p) => sum + (Number(p.account.total_9l) || 0), 0);
      const synth: FollowUpTrackerResponse = {
        reference_date: inner.data.reference_date,
        buckets: [
          {
            label: TODAY_PLAN_BUCKET_LABEL,
            count: picked.length,
            total_9l: totalCases.toString(),
            accounts: picked.map((p) => p.account),
          },
        ],
        // Pass the upstream territory summary + data window through
        // unchanged so both tabs (Today's Plan + Tracker) show the
        // same counts and time-range context at the top — they're
        // answering the same "how big is my territory" question.
        territory: inner.data.territory ?? null,
        data_window: inner.data.data_window ?? null,
      };
      return synth;
    },
    enabled: !!inner.data,
    staleTime: 30_000,
  });
}

// =====================================================================
// Momentum — the rep home page panel
// =====================================================================
/** How a single day on the strip reads.
 *
 *   logged  real work was recorded — a visit, call, email, event, or
 *           an account flagged for review
 *   seen    the rep opened the app but logged nothing
 *   quiet   neither
 *   future  hasn't happened yet — the runway ahead of today
 */
type MomentumDayState = 'logged' | 'seen' | 'quiet' | 'future';

export interface MomentumDay {
  day: string;
  state: MomentumDayState;
  visits: number;
  is_weekend: boolean;
  /** They opened the app that day — true on `logged` days too. */
  opened: boolean;
  /** Accounts flagged for admin review that day. */
  flags: number;
  /** How the visits were made, keyed by channel. */
  channels: Record<string, number>;
  /** A sample of the places reached, and how many there were. */
  accounts: string[];
  account_total: number;
}

/** One account reached in the window, with its context. */
export interface VisitedAccount {
  name: string;
  visits: number;
  city: string | null;
  state_code: string | null;
  last_visit_on: string;
  last_visit_by: string;
}

/** One hand-added account, with who entered it and when. */
export interface AddedAccount {
  name: string;
  added_by: string;
  added_on: string;
}

export interface Momentum {
  window_days: number;
  /** Days past today the grid draws as unfilled runway. Never counted. */
  lookahead_days: number;
  active_days: number;
  seen_days: number;
  current_run: number;
  /** Oldest first, exactly `window_days` entries, no gaps. */
  days: MomentumDay[];
  visits: number;
  /** How those visits were made, keyed by channel. Channels with
   *  no rows are absent. */
  visits_by_channel: Record<string, number>;
  accounts: number;
  /** A sample of those, busiest first. Capped server-side. */
  account_names: VisitedAccount[];
  new_accounts: number;
  new_account_names: AddedAccount[];
}

/**
 * `days` narrows the measured window — powers the week/month toggle.
 *
 * `actAsId` is not passed to the API — the client attaches the
 * impersonation header itself, and only on rep-surface paths. It is
 * here purely to key the cache by WHOSE data this is.
 *
 * Without it the panel served one rep's figures under another's name.
 * This is the only field query that renders on both an impersonated
 * surface and the personal home page, so it's the only one where the
 * same key can mean two different people. Leaving a rep view by any
 * route other than "Exit rep view" — clicking the logo, say — left the
 * impersonated data cached and the home page read straight from it.
 */
export function useMomentum(days?: number, actAsId?: string | null): UseQueryResult<Momentum> {
  return useQuery({
    queryKey: ['field', 'momentum', days ?? 'default', actAsId ?? 'self'],
    queryFn: () => apiGet<Momentum>('sales', '/api/field/momentum', days ? { days } : undefined),
    placeholderData: (prev) => prev,
    // The window only changes when the rep logs something, and every
    // mutation that could move it already invalidates this key. A
    // minute of staleness keeps tab-switching from refetching it.
    staleTime: 60_000,
  });
}

/**
 * Team-wide totals for the same window — aggregates only, no names.
 *
 * Visible to everyone, which is exactly why it carries no per-person
 * breakdown: a shared surface that ranks people puts somebody at the
 * bottom of it every week.
 */
export interface TeamPulse {
  window_days: number;
  /** The window's real endpoints, resolved in the business timezone. */
  since: string;
  until: string;
  total_reps: number;
  active_reps: number;
  /** Who logged work, alphabetically. Names only — never per-person
   *  counts, and never the reps who were quiet. */
  active_rep_names: string[];
  visits: number;
  /** How those visits were made, keyed by channel. Channels with
   *  no rows are absent. */
  visits_by_channel: Record<string, number>;
  accounts: number;
  /** A sample of those, busiest first. Capped server-side. */
  account_names: VisitedAccount[];
  new_accounts: number;
  new_account_names: AddedAccount[];
}

/** `days` overrides the configured window — powers the week/month toggle. */
export function useTeamPulse(days?: number, actAsId?: string | null): UseQueryResult<TeamPulse> {
  return useQuery({
    // Identity-keyed even though the response is a company-wide
    // aggregate today. The request carries the act-as header inside a
    // rep view, so the day this endpoint gains any actor scoping, an
    // unkeyed entry would serve one identity's figures to another —
    // silently, and exactly like the leaks this pattern already caused.
    queryKey: ['field', 'team-pulse', days ?? 'default', actAsId ?? 'self'],
    queryFn: () => apiGet<TeamPulse>('sales', '/api/field/team-pulse', days ? { days } : undefined),
    staleTime: 60_000,
    // Keeps the previous range on screen while the new one loads, so
    // toggling doesn't blink the numbers out and back.
    placeholderData: (prev) => prev,
  });
}

export interface AdminRepMomentum {
  user_id: string;
  window_days: number;
  active_days: number;
  visits: number;
  days_since_last_activity: number | null;
}

export interface AdminRepMomentumResponse {
  window_days: number;
  items: AdminRepMomentum[];
}

export function useAdminRepMomentum(enabled = true): UseQueryResult<AdminRepMomentumResponse> {
  return useQuery({
    queryKey: ['admin', 'field', 'momentum'],
    queryFn: () => apiGet<AdminRepMomentumResponse>('sales', '/api/admin/field/momentum'),
    staleTime: 60_000,
    enabled,
  });
}

// =====================================================================
// UI helpers
// =====================================================================
export const VISIT_CHANNEL_LABELS: Record<VisitChannel, string> = {
  visit: 'In-person visit',
  call: 'Phone call',
  email: 'Email',
  event: 'Event',
};

export const VISIT_OUTCOME_LABELS: Record<VisitOutcome, string> = {
  ordered: 'Ordered',
  follow_up_needed: 'Follow-up needed',
  no_response: 'No response',
  declined: 'Declined',
  info_only: 'Info only',
};

export const VISIT_OUTCOME_TONES: Record<VisitOutcome, string> = {
  ordered: '#22865a',
  follow_up_needed: '#a86b1e',
  no_response: '#7a7a78',
  declined: '#a13a3a',
  info_only: '#326eb8',
};
